import { useCallback, useEffect, useRef, useState } from 'react';

import { useIntl } from 'react-intl';

import { Form, notification } from 'antd';

import type { OptionList } from '@src/models/interfaces/optionList.interface';
import { OptionListMapper } from '@src/models/mappers/optionList.mapper';
import {
  OPTION_LIST_LABEL_MAX_LENGTH,
  OPTION_LIST_MAX_ITEMS,
} from '@src/packages/optionLists/constants';
import type OptionListService from '@src/services/OptionListService';

interface OptionListFormValues {
  name: string;
  items: string;
}

interface UseOptionListFormParams {
  organizationId: string;
  listId: string | null;
  /** Owning division of a list being created; omitted for an org-wide list. */
  divisionId?: string | null;
  service: OptionListService;
  /** `savedListId` is the created or updated list when `saved` is true. */
  onClose: (saved: boolean, savedListId?: string) => void;
}

export const useOptionListForm = ({ organizationId, listId, divisionId, service, onClose }: UseOptionListFormParams) => {
  const intl = useIntl();
  const [form] = Form.useForm<OptionListFormValues>();
  const [list, setList] = useState<OptionList | null>(null);
  const [loading, setLoading] = useState(Boolean(listId));
  const [saving, setSaving] = useState(false);
  const itemsText = Form.useWatch('items', form) ?? '';
  const itemCount = OptionListMapper.labelsFromLines(itemsText).length;
  // Callers may pass a new `onClose` each render; the load must not re-run and overwrite typing.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!listId) return undefined;
    let cancelled = false;
    // Without the stored list, a save would create a second one: close instead.
    const failLoad = () => {
      if (cancelled) return;
      notification.error({ message: intl.formatMessage({ id: 'optionLists.form.load.error' }) });
      onCloseRef.current(false);
    };
    service
      .get(organizationId, listId)
      .then(result => {
        if (cancelled) return;
        if (!result) {
          failLoad();
          return;
        }
        setList(result);
        form.setFieldsValue({
          name: result.name,
          items: result.items.filter(item => item.active).map(item => item.label).join('\n'),
        });
      })
      .catch(failLoad)
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [form, intl, listId, organizationId, service]);

  const validateItems = useCallback(
    async (_: unknown, value: string | undefined) => {
      const labels = OptionListMapper.labelsFromLines(value ?? '');
      if (labels.length === 0) {
        throw new Error(intl.formatMessage({ id: 'optionLists.form.items.required' }));
      }
      if (labels.length > OPTION_LIST_MAX_ITEMS) {
        throw new Error(intl.formatMessage({ id: 'optionLists.form.items.tooMany' }, { max: OPTION_LIST_MAX_ITEMS }));
      }
      const tooLong = labels.find(label => label.length > OPTION_LIST_LABEL_MAX_LENGTH);
      if (tooLong) {
        throw new Error(
          intl.formatMessage({ id: 'optionLists.form.items.tooLong' }, { label: tooLong, max: OPTION_LIST_LABEL_MAX_LENGTH }),
        );
      }
    },
    [intl],
  );

  /** Reads a CSV or text file into the items box; returns false so antd does not upload it. */
  const importFile = useCallback(
    (file: File) => {
      file
        .text()
        .then(text => {
          form.setFieldsValue({ items: OptionListMapper.labelsFromCsv(text).join('\n') });
          form.validateFields(['items']).catch(() => undefined);
        })
        .catch(() => {
          notification.error({ message: intl.formatMessage({ id: 'optionLists.form.import.error' }) });
        });
      return false;
    },
    [form, intl],
  );

  const onSubmit = useCallback(
    async (values: OptionListFormValues) => {
      // Editing: never fall back to creating while the stored list is not loaded.
      if (listId && !list) return;
      const labels = OptionListMapper.labelsFromLines(values.items ?? '');
      setSaving(true);
      try {
        let savedListId: string;
        if (list) {
          await service.update(organizationId, list, values.name, labels);
          savedListId = list.id;
        } else {
          savedListId = await service.create(organizationId, values.name, labels, divisionId);
        }
        notification.success({ message: intl.formatMessage({ id: 'general.save.successful.message' }) });
        onClose(true, savedListId);
      } catch {
        notification.error({ message: intl.formatMessage({ id: 'general.save.error.message' }) });
      } finally {
        setSaving(false);
      }
    },
    [divisionId, intl, list, listId, onClose, organizationId, service],
  );

  return { form, loading, saving, itemCount, onSubmit, importFile, validateItems };
};
